/**
 * useCoachPageContext — builds the human-readable "where is the learner and
 * what are they looking at" string the live avatar coach ships to the
 * ElevenLabs agent as a silent contextual update (never spoken or shown).
 *
 * Reads ONLY data that is already on the client: React Router's match tree
 * (`useMatches`) exposes every active route's loader data from anywhere in the
 * component tree, so the root-mounted coach can see the program-day loader
 * payload and the annotate article without any new fetch, provider, or cache
 * work. Route params come from the matches too (`useParams` is empty at root).
 *
 * The strings are deliberately English-only: they are agent context, not UI
 * copy — the agent handles response language itself (no Lingui involvement).
 */
import { useMemo } from "react";
import { useLocation, useMatches } from "react-router";

const DAY_ROUTE_ID = "routes/_app.programs.$slug.$cohortID.$sectionID.$dayID";
const ANNOTATE_ROUTE_ID = "routes/_annotate.annotate.$articleId";

// Content excerpts are capped so a long article can't blow up the contextual
// update; the agent only needs enough to ground the conversation.
const CONTENT_EXCERPT_MAX = 1_200;
const TRANSCRIPT_EXCERPT_MAX = 1_200;

// Local mirror of the day route's getCardFromRoutePath — NOT imported from the
// route module, so the root-mounted coach doesn't pull the code-split route
// chunk (GraphQL docs + page components) into the root bundle.
const CARD_LABELS: Record<string, string> = {
  start: "Start",
  intro: "Overview",
  concepts: "Key Concept",
  perspective: "Perspective",
  practical: "Practical Application",
  "practical-screenshot": "Practical Application",
  "practical-sandbox": "Practical Application (AI sandbox)",
  trivia: "Trivia quiz",
  extension: "Extension",
  references: "References",
  weekdone: "Week Done",
  learningcalendar: "Learning Calendar",
  assessment: "Assessment",
};

// Mint-time card derivation (Amendment 2.1): the docked rail-tab launcher is
// the sole coach entry point, so the mount derives the card locator from the
// CURRENT route when it mints — a coach open on a day content card mints
// card-walkthrough mode; every other segment (start, weekdone, references, …)
// and every non-day route derives nothing and mints DEFAULT mode. Keys are
// route segments, values the server-allowlisted mint segments (Amendment 1-B:
// the ids only SELECT server-derived context, validated against the caller's
// own enrollment); the practical route variants all mint as "practical".
const MINT_CARD_SEGMENTS: Record<string, string> = {
  intro: "intro",
  concepts: "concepts",
  perspective: "perspective",
  practical: "practical",
  "practical-screenshot": "practical",
  "practical-sandbox": "practical",
  trivia: "trivia",
};

/* Structural slice of react-router's UIMatch — enough to read the day route's
 * params without importing router types. */
interface RouteMatchLike {
  id: string;
  params: { readonly [key: string]: string | undefined };
}

/** Card locator for a card-mode mint — the `card` field of the
 * `AiCoachAvatarSession` mutation's input. */
export interface CoachCardLocator {
  cohortID: string;
  dayID: string;
  card: string;
}

/**
 * The card the learner is looking at RIGHT NOW, as a mint locator — or null
 * when the current route is not a day content card. Pure so the mount can call
 * it inside a memo; shares this file's route-matching approach (day route id
 * from the match tree + last path segment) rather than duplicating the map a
 * third time.
 */
export function deriveCoachCardLocator(
  matches: readonly RouteMatchLike[],
  pathname: string,
): CoachCardLocator | null {
  const dayMatch = matches.find((m) => m.id === DAY_ROUTE_ID);
  if (!dayMatch) return null;
  const { cohortID, dayID } = dayMatch.params;
  if (!cohortID || !dayID) return null;
  const card = MINT_CARD_SEGMENTS[pathname.split("/").pop() ?? ""];
  if (!card) return null;
  return { cohortID, dayID, card };
}

/* Minimal structural slices of the two loaders' data — enough to format the
 * context without importing the route modules or generated query types. */
interface DayLoaderData {
  programDayData?: {
    mindspace?: {
      programs?: {
        program?: {
          metadata?: { title?: string | null; slug?: string | null } | null;
          cohort?: {
            progress?: number | null;
            section?: {
              item?: {
                metadata?: {
                  title?: string | null;
                  sectionTitle?: string | null;
                } | null;
                content?: {
                  __typename?: string;
                  title?: string | null;
                  introduction?: string | null;
                  introductionCallout?: string | null;
                  summary?: string | null;
                  summaryCallout?: string | null;
                  references?: ({ headline?: string | null } | null)[] | null;
                  trivia?: {
                    questions?:
                      | ({
                          question?: string | null;
                          answers?:
                            | ({ answer?: string | null } | null)[]
                            | null;
                        } | null)[]
                      | null;
                  } | null;
                  triviaFreetext?: {
                    question?: { question?: string | null } | null;
                  } | null;
                } | null;
              } | null;
            } | null;
          } | null;
        } | null;
      } | null;
    } | null;
  } | null;
}

interface AnnotateLoaderData {
  article?: {
    headline?: string | null;
    author?: string | null;
    siteName?: string | null;
    type?: string | null;
    textContent?: string | null;
    caption?: {
      caption?: ({ text?: string | null } | null)[] | null;
    } | null;
  } | null;
}

function excerpt(value: string | null | undefined, max: number): string {
  if (!value) return "";
  const cleaned = value.replace(/\s+/g, " ").trim();
  if (cleaned.length <= max) return cleaned;
  return `${cleaned.slice(0, max - 1).trimEnd()}…`;
}

function dayContext(data: DayLoaderData, pathname: string): string | null {
  const program = data.programDayData?.mindspace?.programs?.program;
  const item = program?.cohort?.section?.item;
  if (!item) return null;

  const cardSegment = pathname.split("/").pop() ?? "";
  const cardLabel = CARD_LABELS[cardSegment] ?? "";
  const content = item.content;

  // Show the agent the text of the card the learner is actually looking at.
  let cardContent = "";
  switch (cardSegment) {
    case "intro":
    case "start":
      cardContent = excerpt(content?.introduction, CONTENT_EXCERPT_MAX);
      break;
    case "concepts":
      cardContent = excerpt(content?.introductionCallout, CONTENT_EXCERPT_MAX);
      break;
    case "perspective":
      cardContent = excerpt(content?.summary, CONTENT_EXCERPT_MAX);
      break;
    case "practical":
    case "practical-screenshot":
    case "practical-sandbox":
      cardContent = excerpt(content?.summaryCallout, CONTENT_EXCERPT_MAX);
      break;
    case "references": {
      const headlines = (content?.references ?? [])
        .map((r) => r?.headline)
        .filter(Boolean)
        .join("; ");
      cardContent = excerpt(headlines, CONTENT_EXCERPT_MAX);
      break;
    }
    case "trivia": {
      // Amendment 1-G: translated question text + option texts ONLY — no
      // correctness/answers/prior results, and no positional references (the
      // options are shuffled client-side). Same "Question: … Options: a | b"
      // shape as the server's mint-time trivia serializer.
      const parts: string[] = [];
      const mcq = content?.trivia?.questions?.[0];
      if (mcq?.question) {
        const options = (mcq.answers ?? [])
          .map((a) => a?.answer)
          .filter(Boolean)
          .join(" | ");
        parts.push(
          options
            ? `Question: ${mcq.question} Options: ${options}`
            : `Question: ${mcq.question}`,
        );
      }
      const freetextQuestion = content?.triviaFreetext?.question?.question;
      if (freetextQuestion) {
        parts.push(`Question: ${freetextQuestion}`);
      }
      cardContent = excerpt(parts.join(" "), CONTENT_EXCERPT_MAX);
      break;
    }
    default:
      cardContent = excerpt(
        content?.introduction || content?.introductionCallout,
        CONTENT_EXCERPT_MAX,
      );
  }

  const parts = [
    "The learner is currently on a learning activity page.",
    program?.metadata?.title ? `Program: "${program.metadata.title}".` : "",
    item.metadata?.sectionTitle ? `Week/section: "${item.metadata.sectionTitle}".` : "",
    item.metadata?.title ? `Activity: "${item.metadata.title}".` : "",
    cardLabel ? `They are viewing the "${cardLabel}" card of this activity.` : "",
    typeof program?.cohort?.progress === "number"
      ? `Their overall program progress is ${program.cohort.progress}%.`
      : "",
    cardContent ? `Content of the current card: ${cardContent}` : "",
  ];
  return parts.filter(Boolean).join(" ");
}

function annotateContext(data: AnnotateLoaderData): string | null {
  const article = data.article;
  if (!article) return null;

  const body = excerpt(article.textContent, CONTENT_EXCERPT_MAX);
  const transcript = body
    ? ""
    : excerpt(
        (article.caption?.caption ?? [])
          .map((c) => c?.text)
          .filter(Boolean)
          .join(" "),
        TRANSCRIPT_EXCERPT_MAX,
      );

  const parts = [
    "The learner is currently reading/watching a learning resource in the resource viewer.",
    article.headline ? `Title: "${article.headline}".` : "",
    article.author ? `Author: ${article.author}.` : "",
    article.siteName ? `Source: ${article.siteName}.` : "",
    body ? `Content excerpt: ${body}` : "",
    transcript ? `Video/audio transcript excerpt: ${transcript}` : "",
  ];
  return parts.filter(Boolean).join(" ");
}

function fallbackContext(pathname: string): string {
  if (pathname.startsWith("/sandbox")) {
    return "The learner is currently in the AI Sandbox — a practice area for trying prompts with an AI assistant.";
  }
  if (pathname.startsWith("/use-cases")) {
    return "The learner is currently browsing the AI use-case library.";
  }
  if (pathname.startsWith("/personalizer")) {
    return "The learner is currently in the Personalizer chat, personalising their program.";
  }
  if (/^\/programs\/[^/]+\/[^/]+/.test(pathname)) {
    return "The learner is currently on a program overview page, looking at their week-by-week curriculum and progress.";
  }
  if (pathname.startsWith("/programs")) {
    return "The learner is currently on the programs list page.";
  }
  if (pathname === "/" || pathname === "") {
    return "The learner is currently on their home dashboard.";
  }
  return `The learner is currently on the page ${pathname} of the Mindstone learning platform.`;
}

/**
 * The current page described for the coach, recomputed on navigation. Always
 * returns a non-empty string; the widget dedupes + sends it silently.
 */
export function useCoachPageContext(): string {
  const matches = useMatches();
  const { pathname } = useLocation();

  return useMemo(() => {
    const dayMatch = matches.find((m) => m.id === DAY_ROUTE_ID);
    if (dayMatch?.data) {
      const ctx = dayContext(dayMatch.data as DayLoaderData, pathname);
      if (ctx) return ctx;
    }
    const annotateMatch = matches.find((m) => m.id === ANNOTATE_ROUTE_ID);
    if (annotateMatch?.data) {
      const ctx = annotateContext(annotateMatch.data as AnnotateLoaderData);
      if (ctx) return ctx;
    }
    return fallbackContext(pathname);
  }, [matches, pathname]);
}
